"use client";
import Link from "next/link";
import { Bell, Check } from "lucide-react";
import { useState } from "react";
import { AccountClientError, notificationRequest } from "@/lib/customy/account-client";
import type { CategoryId } from "@/lib/comparison-catalog";
import { accountClientMessage } from "@/components/account-save";
import type { Locale } from "@/lib/i18n/config";
import { localePath } from "@/lib/i18n/routes";

const es = {
  pickOne: "Selecciona al menos un tipo de cambio.",
  activated: "Alerta activada. Puedes gestionarla desde tu cuenta.",
  failed: "No pudimos activar la alerta. Inténtalo de nuevo.",
  optionsLabel: "Cambios que quiero seguir",
  price: "Precio o condiciones",
  benchmarks: "Benchmarks",
  activating: "Activando…",
  active: "Alerta activa",
  create: "Crear alerta",
  goToAccount: "Ir a mi cuenta →",
  note: "Solo usaremos tu correo verificado para esta alerta. Sin marketing.",
};
const en: typeof es = {
  pickOne: "Select at least one type of change.",
  activated: "Alert on. You can manage it from your account.",
  failed: "We couldn't turn on the alert. Please try again.",
  optionsLabel: "Changes I want to follow",
  price: "Pricing or terms",
  benchmarks: "Benchmarks",
  activating: "Turning on…",
  active: "Alert on",
  create: "Create alert",
  goToAccount: "Go to my account →",
  note: "We'll only use your verified email for this alert. No marketing.",
};
const pt: typeof es = {
  pickOne: "Selecione pelo menos um tipo de mudança.",
  activated: "Alerta ativado. Você pode gerenciá-lo na sua conta.",
  failed: "Não conseguimos ativar o alerta. Tente novamente.",
  optionsLabel: "Mudanças que quero acompanhar",
  price: "Preço ou condições",
  benchmarks: "Benchmarks",
  activating: "Ativando…",
  active: "Alerta ativo",
  create: "Criar alerta",
  goToAccount: "Ir para minha conta →",
  note: "Usaremos seu e-mail verificado apenas para este alerta. Sem marketing.",
};
const copy = { es, en, pt };

export function AccountAlert({ category, productId, locale = "es" }: { category: CategoryId; productId: string; locale?: Locale }) {
  const text = copy[locale];
  const [priceChanges, setPriceChanges] = useState(true), [benchmarkChanges, setBenchmarkChanges] = useState(true);
  const [busy, setBusy] = useState(false), [saved, setSaved] = useState(false), [message, setMessage] = useState(""), [needsAccount, setNeedsAccount] = useState(false);
  const submit = async () => {
    if (!priceChanges && !benchmarkChanges) { setMessage(text.pickOne); return; }
    setBusy(true); setMessage(""); setNeedsAccount(false);
    try {
      await notificationRequest("POST", { category, productId, priceChanges, benchmarkChanges });
      setSaved(true); setMessage(text.activated);
    } catch (error) {
      setMessage(error instanceof AccountClientError ? accountClientMessage(error, locale) : text.failed);
      setNeedsAccount(error instanceof AccountClientError && [401, 403].includes(error.status));
    } finally { setBusy(false); }
  };
  return <div className="vc-account vc-account-alert">
    <div className="vc-alert-options" role="group" aria-label={text.optionsLabel}>
      <label><input type="checkbox" checked={priceChanges} disabled={busy || saved} onChange={event => setPriceChanges(event.target.checked)} /> {text.price}</label>
      <label><input type="checkbox" checked={benchmarkChanges} disabled={busy || saved} onChange={event => setBenchmarkChanges(event.target.checked)} /> {text.benchmarks}</label>
    </div>
    <button className="ui-btn ui-btn-outline ui-btn-sm" type="button" disabled={busy || saved} onClick={submit}>
      {saved ? <Check size={14} /> : <Bell size={14} />}{busy ? text.activating : saved ? text.active : text.create}
    </button>
    <span role="status">{message} {(needsAccount || saved) && <Link href={localePath(locale, "/account")}>{text.goToAccount}</Link>}</span>
    <small>{text.note}</small>
  </div>;
}
